import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TipoSimulacao } from "../../enums/simulation-type.enum";
import { LocalizacaoService } from "../localizacao/localizacao.service";
import { CaqRequestService } from '../caq-request/caq-request.service';
import { DadosRelatorioCaqDto } from '../../dtos/caq-por-etapa-dto';
import { CustoEscolaQualidadeDto } from '../../dtos/custo-escola-qualidade-dto';

@Injectable({
  providedIn: 'root'
})
export class CustoPorEscolaService {
  private apiUrl = `${environment.apiUrl}/simulador/caq`;

  constructor(
    private localizacaoService: LocalizacaoService,
    private caqRequestService: CaqRequestService
  ) { }

  // Busca dados completos de uma escola específica
  async buscarDadosEscola(codMec: number): Promise<CustoEscolaQualidadeDto | null> {
    const url = `${environment.apiUrl}/custo-escola-publica-em-condicao-de-qualidade/json/instituicao/${codMec}`;

    try {
      return await firstValueFrom(
        this.caqRequestService.get<CustoEscolaQualidadeDto>(url)
      );
    } catch (erro) {
      return null;
    }
  }

  // Busca número de TURMAS EXISTENTES de uma escola específica e Retorna 0 se não encontrar
  async buscarTurmasExistentesEscola(codMec: number): Promise<number> {
    const dados = await this.buscarDadosEscola(codMec);
    return dados?.totalDeTurmasExistentes || 0;
  }

  // Busca número de FUNCIONÁRIOS EXISTENTES de uma escola específica e Retorna 0 se não encontrar

  async buscarFuncionariosExistentesEscola(codMec: number): Promise<number> {
    const url = `${environment.apiUrl}/numero-funcionarios/instituicao/${codMec}`;

    try {
      const response = await firstValueFrom(
        this.caqRequestService.get<{ content: { ano: number; quantidade: number } }>(url)
      );

      return response.content?.quantidade || 0;
    } catch (erro) {
      return 0;
    }
  }

  // Busca dados do relatório 4 (Custo por Escola) - CAQ por etapa (Suporta: Nacional, Estado, Município, ou Escola específica)

  buscarDadosRelatorioCustoPorEscola(): Promise<DadosRelatorioCaqDto> {
    const url = this.construirUrlRelatorioCustoPorEscola();

    return firstValueFrom(
      this.caqRequestService.get<DadosRelatorioCaqDto>(url)
    );
  }

  // Constrói URL para buscar dados do relatório R4

  private construirUrlRelatorioCustoPorEscola(): string {
    const localizacao = this.localizacaoService.obterLocalizacaoSelecionada();

    if (!localizacao) {
      return `${this.apiUrl}/nacional`;
    }

    // Se há escola selecionada, busca dados apenas dessa escola
    if (localizacao.escolaSelecionada) {
      const codMec = localizacao.escolaSelecionada.codMec;
      return `${this.apiUrl}/escola/${codMec}`;
    }

    const tipo = localizacao.tipoSimulacaoSelecionado;

    // Nível nacional
    if (tipo === TipoSimulacao.planejamentoNacional) {
      return `${this.apiUrl}/nacional`;
    }

    // Nível municipal
    if (localizacao.cidadeSelecionada) {
      const codMun = localizacao.cidadeSelecionada.value;
      if (tipo === TipoSimulacao.planejamento) {
        return `${this.apiUrl}/plano-municipal/${codMun}`;
      }
      if (tipo === TipoSimulacao.planejamentoPorEsferaAdmMunicipal) {
        return `${this.apiUrl}/rede-municipal/${codMun}`;
      }
    }

    // Nível estadual
    if (localizacao.estadoSelecionado) {
      const codUf = localizacao.estadoSelecionado.value;
      if (tipo === TipoSimulacao.planejamento) {
        return `${this.apiUrl}/plano-estadual/${codUf}`;
      }
      if (tipo === TipoSimulacao.planejamentoPorEsferaAdmEstadual) {
        return `${this.apiUrl}/rede-estatual/${codUf}`;
      }
    }

    throw new Error('Tipo de simulação ou localização inválida para Custo por Escola');
  }
}
